import { z } from 'zod';
import { CREDENTIAL_KIND_NAMES, CREDENTIAL_LIMITS, NODE_CREDENTIAL_KIND_NAMES } from '../domain/credential.js';
import { PLATFORM_NAMES } from '../domain/platform.js';

/** An address a person opens: http or https, nothing a browser would run. */
export const webAddressSchema = z.string().trim().max(CREDENTIAL_LIMITS.url).url()
  .refine((value) => /^https?:\/\//i.test(value), 'Only http and https addresses');

/**
 * What an operator says about a site's platform (ADR-0031). Null for either
 * field hands it back to detection.
 */
export const sitePlatformSchema = z.object({
  platform: z.enum(PLATFORM_NAMES).nullable(),
  adminUrl: webAddressSchema.nullable(),
}).strict();
export type SitePlatformRequest = z.input<typeof sitePlatformSchema>;

/** A form field's name as HTML has it: `username`, `USER_LOGIN`, `login[email]`. */
const formField = z.string().trim().min(1).max(100).regex(/^[A-Za-z0-9_.:[\]-]+$/, 'A form field name');

/** A CMS's own sign-in form (ADR-0042); null: WordPress's, or none. */
export const signInFormSchema = z.object({
  action: webAddressSchema,
  userField: formField,
  passwordField: formField,
  extra: z.record(formField, z.string().max(500)).default({})
    .refine((extra) => Object.keys(extra).length <= 10, 'At most ten fixed fields'),
}).strict().refine((form) => form.userField !== form.passwordField, { message: 'The login and the password are different fields', path: ['passwordField'] });

const optionalText = (max: number) => z.string().trim().max(max).nullable().optional()
  .transform((value) => (value ? value : null));

/** A credential as it is added. The password is optional: a record can hold only where and who. */
export const createCredentialSchema = z.object({
  kind: z.enum(CREDENTIAL_KIND_NAMES),
  label: z.string().trim().min(1).max(CREDENTIAL_LIMITS.label),
  url: z.union([webAddressSchema, z.literal('')]).nullable().optional().transform((value) => (value ? value : null)),
  login: optionalText(CREDENTIAL_LIMITS.login),
  password: z.string().max(CREDENTIAL_LIMITS.password).nullable().optional().transform((value) => (value ? value : null)),
  notes: optionalText(CREDENTIAL_LIMITS.notes),
  signInForm: signInFormSchema.nullable().optional().transform((value) => value ?? null),
}).strict();
export type CreateCredentialInput = z.infer<typeof createCredentialSchema>;
export type CreateCredentialRequest = z.input<typeof createCredentialSchema>;

/**
 * A credential changed. The password is left as it is when absent, replaced
 * when given, and removed when null: a form that did not touch it must not
 * wipe it.
 */
export const updateCredentialSchema = z.object({
  kind: z.enum(CREDENTIAL_KIND_NAMES),
  label: z.string().trim().min(1).max(CREDENTIAL_LIMITS.label),
  url: z.union([webAddressSchema, z.literal('')]).nullable().optional().transform((value) => (value ? value : null)),
  login: optionalText(CREDENTIAL_LIMITS.login),
  password: z.string().min(1).max(CREDENTIAL_LIMITS.password).nullable().optional(),
  notes: optionalText(CREDENTIAL_LIMITS.notes),
  signInForm: signInFormSchema.nullable().optional().transform((value) => value ?? null),
}).strict();
export type UpdateCredentialInput = z.infer<typeof updateCredentialSchema>;
export type UpdateCredentialRequest = z.input<typeof updateCredentialSchema>;

/** A server's credential (ADR-0058): the same fields, the kinds a machine has, no sign-in form. */
export const createNodeCredentialSchema = createCredentialSchema.omit({ signInForm: true }).extend({ kind: z.enum(NODE_CREDENTIAL_KIND_NAMES) });
export type CreateNodeCredentialRequest = z.input<typeof createNodeCredentialSchema>;

export const updateNodeCredentialSchema = updateCredentialSchema.omit({ signInForm: true }).extend({ kind: z.enum(NODE_CREDENTIAL_KIND_NAMES) });
export type UpdateNodeCredentialRequest = z.input<typeof updateNodeCredentialSchema>;
